import { useBusinessEngineApi } from 'infrastructure/api';
import useProcessInstanceService, { ProcessInstance } from 'domain/gpo/cockpit/instance/processInstanceService';

export enum TraceType {
  ACK = 'ACK',
  START = 'START',
}

interface Trace {
  id: string;
  messageName: string;
  correlationKey: string;
  producer: string;
  receiver: string;
  type: TraceType;
}

export type EnrichedTraceRow = Trace & {
  processInstanceId: string;
  businessKey: string;
  processKey: string;
  processVersion: number;
  state: ProcessInstance['state'];
  startDateTime: string;
  endDateTime: string;
  incidentCount: number;
};

export interface EnrichedEventFlow {
  processInstanceId: string;
  startedBy: EnrichedTraceRow[];
  startedProcesses: EnrichedTraceRow[];
  messagesReceived: EnrichedTraceRow[];
  messagesSent: EnrichedTraceRow[];
}

interface EventFlow {
  processInstanceId: string;
  startedBy: Trace[];
  startedProcesses: Trace[];
  messagesReceived: Trace[];
  messagesSent: Trace[];
}

const collectRelatedProcessIds = (eventFlow: EventFlow): string[] => {
  const ids = new Set<string>();

  const addFromTraces = (traces: Trace[]) => {
    traces.forEach(trace => {
      ids.add(trace.producer);
      ids.add(trace.receiver);
    });
  };

  addFromTraces(eventFlow.startedBy);
  addFromTraces(eventFlow.startedProcesses);
  addFromTraces(eventFlow.messagesReceived);
  addFromTraces(eventFlow.messagesSent);

  return [...ids];
};

const fetchProcessInstancesByIds = async (
  processIds: string[],
  getById: (id: string) => Promise<ProcessInstance>,
): Promise<Record<string, ProcessInstance>> => {
  const entries = await Promise.all(
    processIds.map(async processId => {
      try {
        const instance = await getById(processId);
        return [processId, instance] as const;
      } catch {
        return [processId, null] as const;
      }
    }),
  );

  return Object.fromEntries(entries.filter((entry): entry is [string, ProcessInstance] => entry[1] !== null));
};

const toEnrichedRow = (trace: Trace, processId: string, instance?: ProcessInstance): EnrichedTraceRow => ({
  ...trace,
  processInstanceId: processId,
  businessKey: instance?.businessKey ?? '',
  processKey: instance?.processKey ?? '',
  processVersion: instance?.processVersion ?? 0,
  state: instance?.state ?? ('' as ProcessInstance['state']),
  startDateTime: instance?.startDateTime ?? '',
  endDateTime: instance?.endDateTime ?? '',
  incidentCount: instance?.incidentCount ?? 0,
});

const enrichTraces = (
  traces: Trace[],
  linkSource: 'producer' | 'receiver',
  instancesById: Record<string, ProcessInstance>,
): EnrichedTraceRow[] =>
  traces.map(trace => {
    const processId = trace[linkSource];
    return toEnrichedRow(trace, processId, instancesById[processId]);
  });

const enrichEventFlow = (flow: EventFlow, instancesById: Record<string, ProcessInstance>): EnrichedEventFlow => ({
  processInstanceId: flow.processInstanceId,
  startedBy: enrichTraces(flow.startedBy, 'producer', instancesById),
  startedProcesses: enrichTraces(flow.startedProcesses, 'receiver', instancesById),
  messagesReceived: enrichTraces(flow.messagesReceived, 'producer', instancesById),
  messagesSent: enrichTraces(flow.messagesSent, 'receiver', instancesById),
});

const useProcessEventFlowService = () => {
  const { sendGetRequest } = useBusinessEngineApi();
  const { getById } = useProcessInstanceService();

  const getEventFlow = async (processInstanceId: string): Promise<EnrichedEventFlow> => {
    const response = await sendGetRequest(`/camunda/processes/${processInstanceId}/event-flow`);
    const flow: EventFlow = await response.json();
    const processIds = collectRelatedProcessIds(flow);
    const instancesById = await fetchProcessInstancesByIds(processIds, getById);

    return enrichEventFlow(flow, instancesById);
  };

  return { getEventFlow };
};

export default useProcessEventFlowService;
